import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from '@/components/auth/AuthContext';
import { getUserCredits } from '@/services/creditService';
import { Profile } from '@/types/profile';
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  CreditCard,
  User,
  Shield,
  BadgeDollarSign,
  Coins,
  AlertTriangle,
  ChevronDown,
  LineChart,
  Brain,
  Mic,
  FileEdit,
  FolderPlus,
  Tags,
  LogOut,
  UserCheck,
  HelpCircle
} from 'lucide-react';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  expanded: boolean;
  active: boolean;
  isSubmenu?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface AdminMenuProps {
  expanded: boolean;
  location: { pathname: string };
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, to, expanded, active, isSubmenu = false, onClick }) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={to}
            className={`flex items-center py-3 px-4 rounded-lg mb-1 transition-all duration-200 group
              ${active 
                ? 'bg-roundone-purple/20 text-white' 
                : 'text-slate-300 hover:bg-white/10 hover:text-white'
              }
              ${isSubmenu ? 'ml-4' : ''}
            `}
            onClick={onClick}
          >
            <div className={`flex items-center ${expanded ? 'space-x-3 w-full' : 'justify-center w-full'}`}>
              <div className={`${active ? 'text-roundone-light-purple' : 'text-slate-300'}`}>
                {icon}
              </div>
              {expanded && (
                <span className="animate-fade-in whitespace-nowrap overflow-hidden text-sm font-medium">
                  {label}
                </span>
              )}
            </div>
          </Link>
        </TooltipTrigger>
        {!expanded && <TooltipContent side="right">{label}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};

const AdminMenu: React.FC<AdminMenuProps> = ({ expanded, location }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Check if current route is a primary admin route (not blogs)
  const isAdminRoute = location.pathname.startsWith('/admin') && 
                      !location.pathname.startsWith('/admin/blogs') &&
                      (location.pathname === '/admin/credits' || 
                       location.pathname === '/admin/management' ||
                       location.pathname === '/admin/users' || 
                       location.pathname === '/admin/prospects' ||
                       location.pathname === '/admin/reports' || 
                       location.pathname === '/admin/errors' || 
                       location.pathname === '/admin/recruiter-verification');
  
  // Only auto-expand admin menu when on admin routes, not blog routes
  useEffect(() => {
    if (isAdminRoute) {
      setIsOpen(true);
    }
  }, [isAdminRoute]);

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <div className="space-y-1">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={toggleMenu}
              className={`w-full flex items-center py-3 px-4 rounded-lg mb-1 transition-all duration-200 group
                text-slate-300 hover:bg-white/10 hover:text-white
              `}
            >
              <div className={`flex items-center ${expanded ? 'space-x-3 w-full' : 'justify-center w-full'}`}>
                <div className="text-slate-300">
                  <Shield size={20} />
                </div>
                {expanded && (
                  <div className="flex items-center justify-between w-full">
                    <span className="animate-fade-in whitespace-nowrap overflow-hidden text-sm font-medium">
                      Admin
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </div>
                )}
              </div>
            </button>
          </TooltipTrigger>
          {!expanded && <TooltipContent side="right">Admin</TooltipContent>}
        </Tooltip>
      </TooltipProvider>

      {expanded && isOpen && (
        <div className="space-y-1">
          <NavItem
            icon={<BadgeDollarSign size={20} />}
            label="Credit Management"
            to="/admin/credits"
            expanded={expanded}
            active={location.pathname === '/admin/credits'}
            isSubmenu
          />
          <NavItem
            icon={<Shield size={20} />}
            label="Management"
            to="/admin/management"
            expanded={expanded}
            active={location.pathname === '/admin/management' || location.pathname === '/admin/users' || location.pathname === '/admin/prospects'}
            isSubmenu
          />
          <NavItem
            icon={<LineChart size={20} />}
            label="Reports"
            to="/admin/reports"
            expanded={expanded}
            active={location.pathname === '/admin/reports'}
            isSubmenu
          />
          <NavItem
            icon={<AlertTriangle size={20} />}
            label="Error Logs"
            to="/admin/errors"
            expanded={expanded}
            active={location.pathname === '/admin/errors'}
            isSubmenu
          />
          <NavItem
            icon={<UserCheck size={20} />}
            label="Recruiter Verification"
            to="/admin/recruiter-verification"
            expanded={expanded}
            active={location.pathname === '/admin/recruiter-verification'}
            isSubmenu
          />

        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC = () => {
  const [expanded, setExpanded] = useState(() => {
    const savedState = localStorage.getItem('sidebarExpanded');
    return savedState === null ? true : savedState === 'true';
  });
  const [userCredits, setUserCredits] = useState<number | null>(null);
  const [isBlogMenuOpen, setIsBlogMenuOpen] = useState(false);
  const location = useLocation();
  const { user, profile, isCandidate, isRecruiter, isAdmin, signOut } = useAuth();
  
  // Check if the current route is a blog route
  const isBlogRoute = location.pathname.startsWith('/admin/blogs');
  
  // Auto-expand blog menu when navigating to a blog route
  useEffect(() => {
    if (isBlogRoute) {
      setIsBlogMenuOpen(true);
    } else {
      setIsBlogMenuOpen(false);
    }
  }, [isBlogRoute]);

  useEffect(() => {
    if (user) {
      fetchUserCredits();
    }
  }, [user]);

  const fetchUserCredits = async () => {
    if (!user) return;
    try {
      const credits = await getUserCredits(user.id);
      setUserCredits(credits?.balance || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  };

  const toggleSidebar = () => {
    setExpanded(prev => {
      const newState = !prev;
      localStorage.setItem('sidebarExpanded', String(newState));
      return newState;
    });
  };

  const getInitials = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${(profile.first_name || '')[0] || ''}${(profile.last_name || '')[0] || ''}`.toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || 'U';
  };

  const getDisplayName = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return user?.email || 'User';
  };

  const displayRole = () => {
    if (profile?.is_admin === true) {
      return 'Admin';
    }
    if (profile?.user_role === 'candidate') {
      return 'Candidate';
    }
    return 'Recruiter';
  };

  return (
    <div className={`h-screen sticky top-0 flex flex-col ${expanded ? 'w-64' : 'w-20'} bg-roundone-dark-purple transition-all duration-300 ease-in-out border-r border-slate-800`}>
      {/* Sidebar Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
        <div className={expanded ? 'block w-full' : 'hidden'}>
          <Logo variant={expanded ? 'full' : 'icon'} light={true} />
        </div>
        {!expanded && (
          <div className="w-full flex justify-center">
            <Logo variant="icon" light={true} />
          </div>
        )}
      </div>

      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-20 bg-roundone-purple text-white rounded-full p-1 shadow-md hover:bg-roundone-light-purple transition-colors"
      >
        {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
      </button>

      {/* Navigation Links */}
      <div className="flex-1 py-4 px-3 overflow-y-auto">
        <div className="space-y-1">
          <NavItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            to="/dashboard"
            expanded={expanded}
            active={location.pathname === '/dashboard'}
          />
          
          <NavItem
            icon={<FileText size={20} />}
            label="Assessments"
            to="/assessments"
            expanded={expanded}
            active={location.pathname.includes('/assessment') && !location.pathname.includes('interface')}
          />
          <NavItem
            icon={<Users size={20} />}
            label="Candidates"
            to="/candidates"
            expanded={expanded}
            active={location.pathname.includes('/candidates')}
          />
        </div>

        {/* Admin Menu - Only if user has admin role */}
        {isAdmin() && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <AdminMenu
              expanded={expanded}
              location={location}
            />
          </div>
        )}
        
        {/* Blog Management - Only if user has admin role */}
        {isAdmin() && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="space-y-1">
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setIsBlogMenuOpen(!isBlogMenuOpen)}
                      className={`w-full flex items-center py-3 px-4 rounded-lg mb-1 transition-all duration-200 group
                        ${isBlogRoute ? 'bg-roundone-purple/20 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'}
                      `}
                    >
                      <div className={`flex items-center ${expanded ? 'space-x-3 w-full' : 'justify-center w-full'}`}>
                        <div className={`${isBlogRoute ? 'text-roundone-light-purple' : 'text-slate-300'}`}>
                          <FileEdit size={20} />
                        </div>
                        {expanded && (
                          <div className="flex items-center justify-between w-full">
                            <span className="animate-fade-in whitespace-nowrap overflow-hidden text-sm font-medium">
                              Blog Management
                            </span>
                            <ChevronDown
                              size={16}
                              className={`transition-transform duration-200 ${isBlogMenuOpen ? 'rotate-180' : ''}`}
                            />
                          </div>
                        )}
                      </div>
                    </button>
                  </TooltipTrigger>
                  {!expanded && <TooltipContent side="right">Blog Management</TooltipContent>}
                </Tooltip>
              </TooltipProvider>
              
              {expanded && isBlogMenuOpen && (
                <div className="space-y-1 ml-4">
                  <NavItem
                    icon={<FileEdit size={20} />}
                    label="All Blogs"
                    to="/admin/blogs"
                    expanded={expanded}
                    active={location.pathname === '/admin/blogs'}
                    isSubmenu
                  />
                  <NavItem
                    icon={<FolderPlus size={20} />}
                    label="New Blog"
                    to="/admin/blogs/new"
                    expanded={expanded}
                    active={location.pathname === '/admin/blogs/new'}
                    isSubmenu
                  />
                  <NavItem
                    icon={<Tags size={20} />}
                    label="Categories"
                    to="/admin/blogs/categories"
                    expanded={expanded}
                    active={location.pathname === '/admin/blogs/categories'}
                    isSubmenu
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* User Settings Section - Completely separate from main navigation */}
      <div className="border-t border-white/10 px-3 py-4">
        <div className="space-y-1">
          <NavItem
            icon={<Coins size={20} />}
            label={`Credits: ${userCredits !== null ? userCredits : '—'}`}
            to="/credits"
            expanded={expanded}
            active={location.pathname === '/credits'}
          />
          <NavItem
            icon={<HelpCircle size={20} />}
            label="Help & Support"
            to="/support"
            expanded={expanded}
            active={location.pathname === '/support'}
          />
          <NavItem
            icon={<Settings size={20} />}
            label="Settings"
            to="/settings"
            expanded={expanded}
            active={location.pathname === '/settings'}
          />
          <NavItem
            icon={<LogOut size={20} />}
            label="Sign Out"
            to="#"
            expanded={expanded}
            active={false}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              signOut();
            }}
          />
        </div>
      </div>

      {/* User Profile - Clickable to navigate to profile page */}
      <Link to="/profile" className="block p-4 border-t border-white/10 hover:bg-white/5 transition-colors">
        <div className={`flex ${expanded ? 'items-center space-x-3' : 'flex-col items-center'}`}>
          <Avatar>
            <AvatarImage src={profile?.avatar_url || undefined} alt={getDisplayName()} />
            <AvatarFallback className="bg-roundone-purple text-white">{getInitials()}</AvatarFallback>
          </Avatar>
          {expanded && (
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium text-white truncate">{getDisplayName()}</p>
              <p className="text-xs text-slate-300 truncate">{displayRole()}</p>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default Sidebar;